/* Fuego & Fragancia — carrusel que se arrastra con el ratón.

   En táctil el navegador ya desplaza solo (overflow-x + scroll-snap); esto
   añade lo que falta en escritorio: agarrar y tirar con el ratón.

   Se usa como <ff-drag-scroll> envolviendo cualquier contenedor con scroll
   horizontal, indicado en data-scroller. */

if (!customElements.get('ff-drag-scroll')) {
  customElements.define(
    'ff-drag-scroll',
    class FfDragScroll extends HTMLElement {
      connectedCallback() {
        this.scroller = this.querySelector(this.dataset.scroller || '[data-scroller]');
        if (!this.scroller) return;

        this.buildArrows();
        this.markScrollable();

        if ('ResizeObserver' in window) {
          new ResizeObserver(() => {
            this.markScrollable();
            this.updateArrows();
          }).observe(this.scroller);
        }

        this.scroller.addEventListener('scroll', () => this.updateArrows(), { passive: true });

        // CLAVE: los <a> y las <img> son arrastrables de forma nativa. Sin
        // esto, al tirar del carrusel el navegador arranca SU propio arrastre
        // de enlace (con la miniatura fantasma) y se traga el gesto: parecía
        // que el carrusel no respondía y que solo "saltaba el enlace".
        this.scroller.addEventListener('dragstart', (event) => event.preventDefault());

        let dragging = false;
        let captured = false;
        let moved = false;
        let startX = 0;
        let startScroll = 0;

        this.scroller.addEventListener('pointerdown', (event) => {
          if (event.pointerType !== 'mouse') return;
          if (!this.classList.contains('is-scrollable')) return;
          dragging = true;
          moved = false;
          captured = false;
          startX = event.clientX;
          startScroll = this.scroller.scrollLeft;
        });

        this.scroller.addEventListener('pointermove', (event) => {
          if (!dragging) return;
          const delta = event.clientX - startX;

          if (!moved && Math.abs(delta) > 4) {
            moved = true;
            this.classList.add('is-dragging');
            // La captura se pide solo al confirmar arrastre real: pedirla en
            // pointerdown haría que un clic simple no llegara nunca al enlace
            // de la tarjeta.
            this.scroller.setPointerCapture(event.pointerId);
            captured = true;
          }

          if (moved) this.scroller.scrollLeft = startScroll - delta;
        });

        const stop = (event) => {
          if (!dragging) return;
          dragging = false;
          this.classList.remove('is-dragging');
          if (captured && this.scroller.hasPointerCapture?.(event.pointerId)) {
            this.scroller.releasePointerCapture(event.pointerId);
          }
          captured = false;
        };

        this.scroller.addEventListener('pointerup', stop);
        this.scroller.addEventListener('pointercancel', stop);

        // Tras arrastrar, soltar no debe abrir la tarjeta.
        this.scroller.addEventListener(
          'click',
          (event) => {
            if (!moved) return;
            event.preventDefault();
            event.stopPropagation();
            moved = false;
          },
          true
        );
      }

      // Solo se ofrece el cursor de "agarrar" si de verdad hay algo que
      // desplazar; con pocas tarjetas no tiene sentido.
      markScrollable() {
        const can = this.scroller.scrollWidth > this.scroller.clientWidth + 1;
        this.classList.toggle('is-scrollable', can);
        this.updateArrows();
      }

      /* ---------- Flechas ----------
         Se crean desde JS a propósito: así cualquier carrusel que use este
         componente las tiene sin repetir markup en cada sección. */

      buildArrows() {
        const make = (dir, label, path) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = `ff-drag-arrow ff-drag-arrow--${dir}`;
          btn.setAttribute('aria-label', label);
          btn.innerHTML =
            `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"` +
            ` stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="${path}"/></svg>`;
          btn.addEventListener('click', () => this.step(dir === 'prev' ? -1 : 1));
          this.appendChild(btn);
          return btn;
        };

        this.prevBtn = make('prev', this.dataset.labelPrev || 'Anterior', 'm15 18-6-6 6-6');
        this.nextBtn = make('next', this.dataset.labelNext || 'Siguiente', 'm9 18 6-6-6-6');
      }

      // Avanza el ancho de una tarjeta: así el desplazamiento siempre encaja
      // con el scroll-snap y nunca deja una tarjeta cortada.
      step(direction) {
        const card = this.scroller.firstElementChild;
        const gap = parseFloat(getComputedStyle(this.scroller).columnGap || '0') || 0;
        const amount = card ? card.getBoundingClientRect().width + gap : this.scroller.clientWidth * 0.8;
        this.scroller.scrollBy({ left: amount * direction, behavior: 'smooth' });
      }

      // Cada flecha aparece solo cuando lleva a algún sitio: la de la
      // izquierda no existe al principio, ni la de la derecha al final.
      //
      // Con data-arrows="single" se ve UNA flecha a la vez: la de avanzar
      // mientras quede algo por delante, y la de volver en cuanto se llega al
      // final. Es para tiras estrechas —las pestañas del cajón— donde dos
      // flechas taparían media pestaña cada una.
      updateArrows() {
        if (!this.prevBtn || !this.nextBtn) return;

        const can = this.classList.contains('is-scrollable');
        const x = this.scroller.scrollLeft;
        const max = this.scroller.scrollWidth - this.scroller.clientWidth;

        // 2px de margen: los navegadores redondean el scroll y el final casi
        // nunca cae en el valor exacto.
        const atStart = x <= 2;
        const atEnd = x >= max - 2;

        // Se exponen al CSS: así el degradado del borde puede cambiar de lado
        // en vez de seguir difuminando un extremo donde ya no queda nada.
        this.classList.toggle('is-at-start', !can || atStart);
        this.classList.toggle('is-at-end', !can || atEnd);

        if (this.dataset.arrows === 'single') {
          this.prevBtn.classList.toggle('is-hidden', !can || !atEnd);
          this.nextBtn.classList.toggle('is-hidden', !can || atEnd);
          return;
        }

        this.prevBtn.classList.toggle('is-hidden', !can || atStart);
        this.nextBtn.classList.toggle('is-hidden', !can || atEnd);
      }
    }
  );
}
